import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faEye,
  faClock,
  faCalendarCheck,
  faChevronDown,
  faMobileScreen,
  faDesktop,
  faTabletScreenButton,
  faLocationDot,
  faArrowRightLong,
  faSignal,
  faUserGraduate,
  faChalkboardUser,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import 'flag-icons/css/flag-icons.min.css';
import { fromServer } from './Servertime';
import './AdminVisitors.css';

/* =========================================================
   Admin › Visitors — people who opened the PUBLIC website.
   Data comes from SiteSessions / SitePageViews (saved by SiteVisitTracker).
   Times are UTC from the server; shown here in Beirut time.
   - "Visitors" = people not logged in (parents, leads). Logged-in students and
     teachers are counted apart, and so is every visit from a browser where they
     logged in once (the server names those visits). Admin visits are not saved.
   - 0-second visits are not sent by the server at all.
   - "Online now" = on the website in the last 5 minutes + students/teachers
     active in their dashboard (moved here from the Users page).
   ========================================================= */
const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const TZ = 'Asia/Beirut';

const WHO = [
  ['visitors', 'Visitors'],
  ['members', 'Students & teachers'],
  ['all', 'All'],
];
const ROLE = { 2: ['Teacher', faChalkboardUser], 3: ['Student', faUserGraduate] };

const RANGES = [
  ['today', 'Today'],
  ['7d', '7 days'],
  ['30d', '30 days'],
];

// friendly names for the site's pages
const PAGE_NAMES = {
  '/': 'Home',
  '/pricingplans': 'Pricing',
  '/freetrial': 'Free trial',
  '/programs': 'Programs',
  '/aboutus': 'About us',
  '/events': 'Events',
  '/studentachievements': 'Achievements',
  '/curriculum': 'Curriculum',
  '/joinfreeclass': 'Join free class',
  '/login': 'Login',
  '/wedo20': 'WeDo 2.0',
  '/terms': 'Terms',
  '/privacy': 'Privacy',
  '/feedback': 'Parent feedback form',
  '/democlass': 'Demo class',
};
const pageName = (p) => PAGE_NAMES[String(p || '').toLowerCase().replace(/\/$/, '') || '/'] || p;

// real flag pictures (emoji flags show as two letters on Windows)
const flag = (code) =>
  /^[A-Z]{2}$/i.test(code || '') ? <span className={`fi fi-${String(code).toLowerCase()} rmavs-fi`} /> : '🌐';

const num = (n) => Number(n || 0).toLocaleString('en-US');
const pct = (part, whole) => (whole ? Math.round((Number(part || 0) / Number(whole)) * 100) : 0);

const duration = (s) => {
  const sec = Math.max(0, Math.round(Number(s || 0)));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  return m < 60 ? `${m}m ${String(r).padStart(2, '0')}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

// Left right away: 1 page, under 5 seconds, started over a minute ago, no demo, not a member.
// Usually bots, ad checkers, or a link opened by mistake (not real parents).
const isQuickExit = (v) => {
  if (v.UserRole || v.BookedDemo) return false;
  if (Number(v.Pages) > 1 || Number(v.seconds) >= 5) return false;
  const d = v.StartedAt ? new Date(v.StartedAt) : null;
  return !!d && !Number.isNaN(d.getTime()) && Date.now() - d.getTime() > 60000;
};

const timeAgo = (value) => {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const sameDay =
    d.toLocaleDateString('en-CA', { timeZone: TZ }) === new Date().toLocaleDateString('en-CA', { timeZone: TZ });
  return d.toLocaleString('en-US', {
    timeZone: TZ,
    ...(sameDay ? {} : { weekday: 'short', month: 'short', day: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ordinal = (n) => {
  const v = n % 100;
  const s = ['th', 'st', 'nd', 'rd'];
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

// dashboard pages of a logged-in visit: the ones from the visit start until now (server clock -> real time)
const DASH_NAMES = { dashboard: 'Dashboard', myclasses: 'My classes', classes: 'My classes', myschedule: 'Schedule', schedule: 'Schedule', curriculum: 'Curriculum', certificates: 'Certificates', mycertificates: 'Certificates', settings: 'Settings', myearnings: 'Earnings', mystudents: 'My students', democlass: 'Demo class' };
const dashName = (p) => {
  const last = String(p.path || '').replace(/\/+$/, '').split('/').pop().toLowerCase();
  return DASH_NAMES[last] || p.title || p.path || 'Page';
};
const dashOfVisit = (v) => {
  const start = new Date(v.StartedAt).getTime() - 60000;
  return (v.dashboard || [])
    .map((d) => ({ ...d, time: fromServer(d.at) }))
    .filter((d) => d.time && d.time.getTime() >= start)
    .sort((a, b) => a.time - b.time);
};
const profileUrl = (v) => (Number(v.UserRole) === 2 ? `/Admin/teacherprofile/${v.UserId}` : `/Admin/studentprofile/${v.UserId}`);

const placeOf = (v) =>
  [v.City, v.Region && v.Region !== v.City ? v.Region : null, v.Country].filter(Boolean).join(', ');

const deviceIcon = (d) => (d === 'Mobile' ? faMobileScreen : d === 'Tablet' ? faTabletScreenButton : faDesktop);

const chartLabel = (label, range) => {
  if (range === 'today') {
    const h = Number(label);
    return `${h % 12 || 12}${h < 12 ? 'am' : 'pm'}`;
  }
  const d = new Date(`${label}T12:00:00Z`);
  return range === '7d'
    ? d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

/* ---------- small pieces ---------- */
const BarList = ({ rows, total, render, empty }) =>
  rows.length ? (
    <ul className="rmavs-bars">
      {rows.map((r, i) => {
        const value = Number(r.visits ?? r.views ?? 0);
        const width = total ? Math.max(3, Math.round((value / total) * 100)) : 0;
        return (
          <li key={i}>
            <div className="rmavs-bar-text">
              {render(r)}
              <span className="rmavs-bar-num">
                {num(value)} <small>{pct(value, total)}%</small>
              </span>
            </div>
            <span className="rmavs-bar-track" aria-hidden="true">
              <span className="rmavs-bar-fill" style={{ width: `${width}%` }} />
            </span>
          </li>
        );
      })}
    </ul>
  ) : (
    <p className="rmavs-empty">{empty}</p>
  );

/* ---------- Online now ---------- */
const OnlineNow = ({ live: allLive, members }) => {
  // the same student/teacher once only (e.g. two tabs, or before + after logging in)
  const seen = new Set();
  const live = allLive.filter((v) => {
    if (!v.UserId) return true;
    const key = `${v.UserRole}:${v.UserId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const dashOnly = members.filter((m) => !live.some((l) => l.UserId && String(l.UserId) === String(m.IdUser)));
  const count = live.length + dashOnly.length;
  return (
    <section className="rmavs-card rmavs-online">
      <div className="rmavs-card-head">
        <h2>
          <span className="rmavs-online-dot" aria-hidden="true" /> Online now <span className="rmavs-count">{count}</span>
        </h2>
      </div>
      {count === 0 ? (
        <p className="rmavs-empty">Nobody right now.</p>
      ) : (
        <ul className="rmavs-online-list">
          {live.map((v) => {
            const role = ROLE[v.UserRole];
            return (
              <li key={v.SessionId}>
                <span className="rmavs-online-flag" aria-hidden="true">
                  {role ? <FontAwesomeIcon icon={role[1]} /> : flag(v.CountryCode)}
                </span>
                <span className="rmavs-online-main">
                  <strong>{v.UserName || placeOf(v) || 'Visitor'}</strong>
                  <small>
                    on <b>{pageName(v.CurrentPage)}</b> · {v.Pages} {Number(v.Pages) === 1 ? 'page' : 'pages'} ·{' '}
                    <FontAwesomeIcon icon={deviceIcon(v.Device)} />
                  </small>
                </span>
                <span className={`rmavs-tag ${role ? 'is-member' : 'is-new'}`}>{role ? role[0] : v.Source || 'Direct'}</span>
              </li>
            );
          })}
          {dashOnly.map((m) => (
            <li key={`u-${m.IdUser}`}>
              <span className="rmavs-online-flag" aria-hidden="true">
                <FontAwesomeIcon icon={faSignal} />
              </span>
              <span className="rmavs-online-main">
                <strong>
                  {m.FirstName} {m.LastName}
                </strong>
                <small>
                  {Number(m.Type) === 1 ? 'on their free-trial page' : 'in their dashboard'} · active {timeAgo(m.LastSeen)}
                </small>
              </span>
              <span className={`rmavs-tag ${Number(m.Type) === 1 ? 'is-new' : 'is-member'}`}>
                {Number(m.Type) === 1 ? 'Free trial' : 'Logged in'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const AdminVisitors = () => {
  const [range, setRange] = useState('7d');
  const [who, setWho] = useState('visitors');
  const [hideQuick, setHideQuick] = useState(false); // Latest visits: hide "left right away" visits
  const [latestWho, setLatestWho] = useState('all'); // Latest visits: everyone (names shown) or visitors only
  const [online, setOnline] = useState({ live: [], members: [] });
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState({ items: [], hasMore: false });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null); // SessionId shown in detail

  // online now: website (last 5 min) + students/teachers in their dashboard. Every 30s.
  const loadOnline = useCallback(async () => {
    const [l, m] = await Promise.allSettled([
      axios.get(`${API}/api/admin/visitors/live`, auth()),
      axios.get(`${API}/api/admin/students/online`, auth()),
    ]);
    const listOf = (res) => {
      const d = res.status === 'fulfilled' ? res.value?.data?.items || res.value?.data?.data || res.value?.data : [];
      return Array.isArray(d) ? d : [];
    };
    setOnline({ live: listOf(l), members: listOf(m).filter((u) => u.IsOnline) });
  }, []);

  useEffect(() => {
    loadOnline();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') loadOnline();
    }, 20000);
    return () => clearInterval(id);
  }, [loadOnline]);

  // quiet = automatic update in the background (no loading boxes, no error flash)
  const recentCount = useRef(0);
  const load = useCallback(async (r, w, lw, quiet = false) => {
    if (!quiet) {
      setLoading(true);
      setError('');
    }
    try {
      // "Latest visits": everyone (people who logged in show with their name) or visitors only
      const keepMore = quiet && recentCount.current > 50; // don't drop the extra pages you opened
      const [s, v] = await Promise.all([
        axios.get(`${API}/api/admin/visitors/summary?range=${r}&who=${w}`, auth()),
        keepMore ? Promise.resolve(null) : axios.get(`${API}/api/admin/visitors/recent?range=${r}&who=${lw}&offset=0`, auth()),
      ]);
      setStats(s.data);
      if (v) setRecent({ items: v.data?.items || [], hasMore: Boolean(v.data?.hasMore) });
      if (!quiet) setError('');
    } catch {
      if (!quiet) setError("Couldn't load visitor stats. It will try again by itself.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    recentCount.current = recent.items.length;
  }, [recent.items.length]);

  // first load, then a quiet update every minute
  useEffect(() => {
    load(range, who, latestWho);
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load(range, who, latestWho, true);
    }, 60000);
    return () => clearInterval(id);
  }, [range, who, latestWho, load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const v = await axios.get(
        `${API}/api/admin/visitors/recent?range=${range}&who=${latestWho}&offset=${recent.items.length}`,
        auth()
      );
      setRecent((cur) => ({ items: [...cur.items, ...(v.data?.items || [])], hasMore: Boolean(v.data?.hasMore) }));
    } catch {
      setError("Couldn't load more visits.");
    } finally {
      setLoadingMore(false);
    }
  };

  const t = stats?.totals || {};
  const series = useMemo(() => stats?.series || [], [stats]);
  const peak = Math.max(1, ...series.map((s) => s.visits));
  const returning = Math.max(0, Number(t.visitors || 0) - Number(t.newVisitors || 0));
  const sourcesTotal = (stats?.sources || []).reduce((a, s) => a + Number(s.visits || 0), 0);
  const placesTotal = Number(t.visits || 0);
  const pagesTotal = Number(t.pageViews || 0);

  return (
    <div className="rmavs">
      <header className="rmavs-header">
        <div>
          <h1>Visitors</h1>
          <p>People who opened your website. Your own visits (admin) and 0-second visits are not counted.</p>
        </div>
        <span className="rmavs-auto">
          <i aria-hidden="true" /> Live · updates automatically
        </span>
      </header>

      <OnlineNow live={online.live} members={online.members} />

      <div className="rmavs-toolbar">
      <div className="rmavs-tabs" role="tablist" aria-label="Period">
        {RANGES.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={range === key}
            className={range === key ? 'is-on' : ''}
            onClick={() => setRange(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="rmavs-tabs rmavs-who" role="radiogroup" aria-label="Who">
        {WHO.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={who === key}
            className={who === key ? 'is-on' : ''}
            onClick={() => setWho(key)}
          >
            {label}
            {stats?.split && key !== 'all' && <span>{num(stats.split[key])}</span>}
          </button>
        ))}
      </div>
      </div>

      {error && <p className="rmavs-error">{error}</p>}

      {stats?.geo && !stats.geo.ready && (
        <p className="rmavs-note">
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>
            <b>Cities are off.</b> Countries come from the visitor's time zone, but cities need the free city database
            ({stats.geo.problem}). Add the file to <code>backend/geo/</code> and run <code>npm install maxmind</code> in the
            backend. Older visits will get their city automatically after that.
          </span>
        </p>
      )}

      {loading && !stats ? (
        <div className="rmavs-skel" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      ) : stats ? (
        <>
          {/* ---------- headline numbers ---------- */}
          <section className="rmavs-kpis">
            <div className="rmavs-kpi">
              <span className="rmavs-kpi-label">
                <FontAwesomeIcon icon={faUsers} /> {who === 'members' ? 'Students & teachers' : who === 'all' ? 'People' : 'Visitors'}
              </span>
              <strong>{num(t.visitors)}</strong>
              <small>
                {num(t.newVisitors)} new · {num(returning)} returning
              </small>
            </div>
            <div className="rmavs-kpi">
              <span className="rmavs-kpi-label">
                <FontAwesomeIcon icon={faEye} /> Page views
              </span>
              <strong>{num(t.pageViews)}</strong>
              <small>
                {num(t.visits)} visits · {t.visits ? (t.pageViews / t.visits).toFixed(1) : '0'} pages each
              </small>
            </div>
            <div className="rmavs-kpi">
              <span className="rmavs-kpi-label">
                <FontAwesomeIcon icon={faClock} /> Time on site
              </span>
              <strong>{duration(t.avgSeconds)}</strong>
              <small>{pct(t.singlePage, t.visits)}% left after 1 page</small>
            </div>
            <div className="rmavs-kpi is-goal">
              <span className="rmavs-kpi-label">
                <FontAwesomeIcon icon={faCalendarCheck} /> Demo requests
              </span>
              <strong>{num(t.demos)}</strong>
              <small>{pct(t.demos, t.visits)}% of visits</small>
            </div>
          </section>

          {/* ---------- visits over time ---------- */}
          <section className="rmavs-card">
            <div className="rmavs-card-head">
              <h2>Visits {range === 'today' ? 'by hour' : 'by day'}</h2>
              <span className="rmavs-muted">Beirut time</span>
            </div>
            {placesTotal ? (
              <div
                className={`rmavs-chart is-${range}`}
                role="img"
                aria-label={`Visits ${range === 'today' ? 'by hour' : 'by day'}: ${series
                  .map((s) => `${chartLabel(s.label, range)} ${s.visits}`)
                  .join(', ')}`}
              >
                {series.map((s, i) => {
                  const showLabel =
                    range === '7d' || (range === 'today' ? i % 3 === 0 : i % 5 === 0 || i === series.length - 1);
                  return (
                    <div key={s.label} className="rmavs-col" tabIndex={0}>
                      <span className="rmavs-tip" role="tooltip">
                        <b>{chartLabel(s.label, range)}</b>
                        {num(s.visits)} visits · {num(s.visitors)} visitors
                      </span>
                      <span className="rmavs-col-track">
                        <span
                          className={`rmavs-col-fill ${s.visits ? '' : 'is-zero'}`}
                          style={{ height: `${s.visits ? Math.max(4, (s.visits / peak) * 100) : 0}%` }}
                        />
                      </span>
                      <span className={`rmavs-col-label ${showLabel ? '' : 'is-hidden'}`}>
                        {chartLabel(s.label, range)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="rmavs-empty">No visits yet in this period.</p>
            )}
          </section>

          {/* ---------- breakdowns ---------- */}
          <div className="rmavs-grid">
            <section className="rmavs-card">
              <div className="rmavs-card-head">
                <h2>Where they came from</h2>
              </div>
              <BarList
                rows={stats.sources || []}
                total={sourcesTotal}
                empty="No visits yet."
                render={(r) => (
                  <span className="rmavs-bar-name">
                    {r.name}
                    {Number(r.demos) > 0 && (
                      <em className="rmavs-demo-tag">
                        {r.demos} demo{Number(r.demos) === 1 ? '' : 's'}
                      </em>
                    )}
                  </span>
                )}
              />
            </section>

            <section className="rmavs-card">
              <div className="rmavs-card-head">
                <h2>Top pages</h2>
              </div>
              <BarList
                rows={stats.pages || []}
                total={pagesTotal}
                empty="No pages opened yet."
                render={(r) => (
                  <span className="rmavs-bar-name" title={r.name}>
                    {pageName(r.name)}
                  </span>
                )}
              />
            </section>

            <section className="rmavs-card">
              <div className="rmavs-card-head">
                <h2>Locations</h2>
              </div>
              <BarList
                rows={stats.places || []}
                total={placesTotal}
                empty="No locations yet."
                render={(r) => (
                  <span className="rmavs-bar-name">
                    <span aria-hidden="true">{flag(r.CountryCode)}</span>{' '}
                    {r.country === 'Unknown' ? 'Unknown location' : placeOf({ City: r.City, Region: r.Region, Country: r.country })}
                  </span>
                )}
              />
            </section>

            <section className="rmavs-card">
              <div className="rmavs-card-head">
                <h2>Devices</h2>
              </div>
              <BarList
                rows={stats.devices || []}
                total={placesTotal}
                empty="No devices yet."
                render={(r) => (
                  <span className="rmavs-bar-name">
                    <FontAwesomeIcon icon={deviceIcon(r.name)} /> {r.name}
                  </span>
                )}
              />
            </section>
          </div>

          {(stats.campaigns || []).length > 0 && (
            <section className="rmavs-card">
              <div className="rmavs-card-head">
                <h2>Campaigns</h2>
                <span className="rmavs-muted">from tagged links (utm)</span>
              </div>
              <div className="rmavs-table-wrap">
                <table className="rmavs-table">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Ad / post</th>
                      <th>Source</th>
                      <th className="is-num">Visits</th>
                      <th className="is-num">Demos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.campaigns.map((c, i) => (
                      <tr key={i}>
                        <td>{c.campaign}</td>
                        <td>{c.content || '—'}</td>
                        <td>{c.source || '—'}</td>
                        <td className="is-num">{num(c.visits)}</td>
                        <td className="is-num">{num(c.demos)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ---------- latest visits ---------- */}
          <section className="rmavs-card">
            <div className="rmavs-card-head">
              <h2>Latest visits</h2>
              <div className="rmavs-seg" role="radiogroup" aria-label="Show">
                {[
                  ['all', 'Everyone'],
                  ['visitors', 'Visitors only'],
                ].map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={latestWho === k} className={latestWho === k ? 'is-on' : ''} onClick={() => setLatestWho(k)}>
                    {label}
                  </button>
                ))}
              </div>
              <label className="rmavs-quick-toggle">
                <input type="checkbox" checked={hideQuick} onChange={(e) => setHideQuick(e.target.checked)} />
                Hide "left right away" ({recent.items.filter(isQuickExit).length})
              </label>
            </div>

            {recent.items.length === 0 ? (
              <p className="rmavs-empty">No visits yet in this period.</p>
            ) : (
              <ul className="rmavs-visits">
                {recent.items.filter((v) => !(hideQuick && isQuickExit(v))).map((v) => {
                  const isOpen = open === v.SessionId;
                  const place = placeOf(v);
                  const role = ROLE[v.UserRole];
                  return (
                    <li key={v.SessionId} className={`${isOpen ? 'is-open' : ''} ${v.BookedDemo ? 'is-demo' : ''}`}>
                      <button
                        type="button"
                        className="rmavs-visit"
                        onClick={() => setOpen(isOpen ? null : v.SessionId)}
                        aria-expanded={isOpen}
                      >
                        <span className="rmavs-visit-flag" aria-hidden="true">
                          {role ? <FontAwesomeIcon icon={role[1]} /> : flag(v.CountryCode)}
                        </span>
                        <span className="rmavs-visit-main">
                          <strong>{role ? v.UserName || role[0] : place || 'Unknown location'}</strong>
                          <span className="rmavs-visit-meta">
                            {timeAgo(v.StartedAt)} · <FontAwesomeIcon icon={deviceIcon(v.Device)} /> {v.OS} ·{' '}
                            {v.Browser}
                            {role && place ? ` · ${place}` : ''}
                          </span>
                        </span>
                        <span className="rmavs-visit-side">
                          <span className="rmavs-pill">{v.Source || 'Direct'}</span>
                          <span className="rmavs-visit-stats">
                            {v.Pages} {Number(v.Pages) === 1 ? 'page' : 'pages'} · {duration(v.seconds)}
                          </span>
                        </span>
                        <span className="rmavs-visit-tags">
                          {isQuickExit(v) && (
                            <span className="rmavs-tag is-quick" title="1 page, under 5 seconds: usually a bot, an ad checker or a link opened by mistake">
                              Left right away
                            </span>
                          )}
                          {v.BookedDemo ? (
                            <span className="rmavs-tag is-demo">
                              <FontAwesomeIcon icon={faCalendarCheck} /> Booked demo
                            </span>
                          ) : null}
                          {role ? (
                            <span className="rmavs-tag is-member">{role[0]}</span>
                          ) : (
                            <span className={`rmavs-tag ${v.IsNewVisitor ? 'is-new' : ''}`}>
                              {v.IsNewVisitor ? 'New' : `${ordinal(Number(v.visitorVisits) || 2)} visit`}
                            </span>
                          )}
                          <FontAwesomeIcon icon={faChevronDown} className="rmavs-chev" />
                        </span>
                      </button>

                      {isOpen && (
                        <div className="rmavs-visit-detail">
                          {role && (
                            <p className="rmavs-member">
                              <a href={profileUrl(v)} target="_blank" rel="noopener noreferrer">
                                Open {v.UserName || role[0]}'s profile
                              </a>
                              <small>Website first, then their dashboard after logging in</small>
                            </p>
                          )}
                          <ol className="rmavs-path">
                            {v.pages.map((p, i) => (
                              <li key={i}>
                                <span>{pageName(p.path)}</span>
                                <small>
                                  {new Date(p.at).toLocaleTimeString('en-US', {
                                    timeZone: TZ,
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  })}
                                </small>
                                {i < v.pages.length - 1 && (
                                  <FontAwesomeIcon icon={faArrowRightLong} className="rmavs-path-arrow" />
                                )}
                              </li>
                            ))}
                          </ol>
                          {role && dashOfVisit(v).length > 0 && (
                            <ol className="rmavs-path is-dashboard" aria-label="In their dashboard">
                              <li className="rmavs-path-label">
                                <span>Logged in</span>
                                <FontAwesomeIcon icon={faArrowRightLong} className="rmavs-path-arrow" />
                              </li>
                              {dashOfVisit(v).map((d, i, all) => (
                                <li key={i}>
                                  <span>{dashName(d)}</span>
                                  <small>{d.time.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' })}</small>
                                  {i < all.length - 1 && <FontAwesomeIcon icon={faArrowRightLong} className="rmavs-path-arrow" />}
                                </li>
                              ))}
                            </ol>
                          )}
                          <dl className="rmavs-dl">
                            <div>
                              <dt>
                                <FontAwesomeIcon icon={faLocationDot} /> Time zone
                              </dt>
                              <dd>{v.Timezone || '—'}</dd>
                            </div>
                            <div>
                              <dt>Came from</dt>
                              <dd>{v.Referrer || 'Typed the address / bookmark'}</dd>
                            </div>
                            {v.UtmCampaign && (
                              <div>
                                <dt>Campaign</dt>
                                <dd>
                                  {v.UtmCampaign}
                                  {v.UtmContent ? ` · ${v.UtmContent}` : ''}
                                </dd>
                              </div>
                            )}
                            <div>
                              <dt>Language</dt>
                              <dd>{v.Lang || '—'}</dd>
                            </div>
                          </dl>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {recent.hasMore && (
              <button type="button" className="rmavs-more" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Show more visits'}
              </button>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
};

export default AdminVisitors;
